import { useEffect, useState, type FormEvent } from "react";
import { api, type User as AuthUser } from "../lib/api";

type Role = "administrator" | "operator" | "viewer";
interface ManagedUser {
  id: string;
  username: string;
  displayName: string;
  active: boolean;
  failedLoginCount: number;
  lockedUntil: string | null;
  lastLoginAt: string | null;
  roles: Role[];
}
interface LoginEvent { id: number; eventType: string; ipAddress: string | null; userAgent: string | null; occurredAt: string }
interface SecurityPolicy { sessionTimeoutMinutes: number; maxLoginFailures: number; lockMinutes: number }
interface SystemData { session: SecurityPolicy }

const roles: Role[] = ["administrator", "operator", "viewer"];
const roleNames: Record<Role, string> = { administrator: "관리자", operator: "운영자", viewer: "감시자" };

export function UsersPage({ currentUser }: { currentUser: AuthUser }) {
  const canManage = currentUser.permissions.includes("user.manage");
  const canCreate = canManage || currentUser.permissions.includes("user.create");
  const canConfigureSecurity = currentUser.permissions.includes("system.configure");
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [selected, setSelected] = useState<ManagedUser | null>(null);
  const [loginEvents, setLoginEvents] = useState<LoginEvent[]>([]);
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [security, setSecurity] = useState<SecurityPolicy | null>(null);
  const [message, setMessage] = useState("");

  const load = async () => {
    const rows = await api<ManagedUser[]>("/api/users");
    setUsers(rows);
    setSelected((current) => current ? rows.find((user) => user.id === current.id) ?? null : null);
  };

  useEffect(() => {
    void load().catch((error) => setMessage(error instanceof Error ? error.message : "사용자 조회 실패"));
    if (canConfigureSecurity) {
      void api<SystemData>("/api/system")
        .then((result) => setSecurity(result.session))
        .catch((error) => setMessage(error instanceof Error ? error.message : "잠금 정책 조회 실패"));
    }
  }, [canConfigureSecurity]);

  const changePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const currentPassword = String(data.get("currentPassword") ?? "");
    const newPassword = String(data.get("newPassword") ?? "");
    const confirmation = String(data.get("newPasswordConfirm") ?? "");
    if (newPassword !== confirmation) {
      setMessage("새 비밀번호와 확인 값이 일치하지 않습니다.");
      return;
    }
    try {
      await api("/api/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
      form.reset();
      setMessage("비밀번호를 변경했습니다. 다른 기기에서 로그인한 세션은 종료됩니다.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "비밀번호 변경 실패");
    }
  };

  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const password = String(data.get("password") ?? "");
    if (password !== String(data.get("passwordConfirm") ?? "")) {
      setMessage("초기 비밀번호와 확인 값이 일치하지 않습니다.");
      return;
    }
    const selectedRoles = canManage ? roles.filter((role) => data.get(`role-${role}`)) : undefined;
    if (canManage && !selectedRoles?.length) {
      setMessage("역할을 하나 이상 선택하세요.");
      return;
    }
    try {
      await api("/api/users", {
        method: "POST",
        body: JSON.stringify({
          username: data.get("username"),
          displayName: data.get("displayName"),
          password,
          ...(selectedRoles ? { roles: selectedRoles } : {})
        })
      });
      form.reset();
      setMessage(canManage ? "사용자를 생성했습니다." : "감시자 계정을 생성했습니다. 카메라 접근 권한은 관리자에게 요청하세요.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "사용자 생성 실패");
    }
  };

  const save = async () => {
    if (!selected || !canManage) return;
    if (!selected.roles.length) {
      setMessage("역할을 하나 이상 선택하세요.");
      return;
    }
    try {
      await api(`/api/users/${selected.id}`, {
        method: "PATCH",
        body: JSON.stringify({ displayName: selected.displayName, active: selected.active, roles: selected.roles })
      });
      setMessage("사용자 정보를 저장했습니다.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "저장 실패");
    }
  };

  const unlock = async (user: ManagedUser) => {
    try {
      await api(`/api/users/${user.id}`, { method: "PATCH", body: JSON.stringify({ unlock: true }) });
      setMessage(isLocked(user) ? "계정 잠금을 해제했습니다." : "로그인 실패 횟수를 초기화했습니다.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "잠금 해제 실패");
    }
  };

  const resetPassword = async (user: ManagedUser) => {
    if (!resetPasswordValue) return;
    if (resetPasswordValue !== resetPasswordConfirm) {
      setMessage("새 비밀번호와 확인 값이 일치하지 않습니다.");
      return;
    }
    try {
      await api(`/api/users/${user.id}/reset-password`, { method: "POST", body: JSON.stringify({ password: resetPasswordValue }) });
      setResetPasswordValue("");
      setResetPasswordConfirm("");
      setMessage("비밀번호를 초기화하고 계정 잠금을 해제했습니다.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "비밀번호 초기화 실패");
    }
  };

  const remove = async (user: ManagedUser) => {
    if (!window.confirm(`${user.displayName} (${user.username}) 계정을 삭제합니까?`)) return;
    try {
      await api(`/api/users/${user.id}`, { method: "DELETE" });
      setSelected(null);
      setMessage("사용자를 삭제했습니다.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "삭제 실패");
    }
  };

  const manage = async (user: ManagedUser) => {
    setSelected({ ...user });
    setResetPasswordValue("");
    setResetPasswordConfirm("");
    try {
      setLoginEvents(await api<LoginEvent[]>(`/api/users/${user.id}/login-events`));
    } catch {
      setLoginEvents([]);
    }
  };

  const toggleRole = (role: Role) => setSelected((user) => user
    ? { ...user, roles: user.roles.includes(role) ? user.roles.filter((item) => item !== role) : [...user.roles, role] }
    : user);

  const saveSecurity = async () => {
    if (!security) return;
    try {
      await api("/api/system/security", { method: "PUT", body: JSON.stringify(security) });
      setMessage("로그인 실패 및 계정 잠금 기준을 저장했습니다.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "잠금 기준 저장 실패");
    }
  };

  return <>
    <div className="page-heading">
      <div><p className="eyebrow">계정 관리</p><h2>사용자</h2></div>
      <span className="text-secondary">{canManage ? `${users.length}명` : "내 계정"}</span>
    </div>
    {message && <div className="alert alert-secondary py-2" role="status">{message}</div>}

    <div className="row g-4">
      <div className="col-xl-8"><div className="panel">
        <div className="panel-title"><h3>{canManage ? "사용자 목록" : "내 계정"}</h3></div>
        <div className="table-responsive"><table className="table table-dark align-middle mb-0">
          <thead><tr><th>계정</th><th>역할</th><th>상태</th><th>마지막 로그인</th><th /></tr></thead>
          <tbody>{users.map((user) => <tr key={user.id}>
            <td><strong>{user.displayName}</strong><div className="small text-secondary">{user.username}</div></td>
            <td>{user.roles.map((role) => <span className="badge text-bg-secondary me-1" key={role}>{roleNames[role]}</span>)}</td>
            <td>
              {user.active ? <span className="badge text-bg-success">활성</span> : <span className="badge text-bg-dark">비활성</span>}
              {isLocked(user) && <span className="badge text-bg-danger ms-1">잠김</span>}
              <div className="small text-secondary">로그인 실패 {user.failedLoginCount}회{isLocked(user) ? ` · ${formatDate(user.lockedUntil)}까지` : ""}</div>
            </td>
            <td>{formatDate(user.lastLoginAt)}</td>
            <td className="text-end"><button className="btn btn-sm btn-outline-light" onClick={() => void manage(user)}>{canManage ? "관리" : "로그인 기록"}</button></td>
          </tr>)}</tbody>
        </table></div>
      </div></div>

      <div className="col-xl-4 d-grid gap-4 align-content-start">
        <form className="panel" onSubmit={changePassword}>
          <div className="panel-title"><h3>내 비밀번호 변경</h3></div>
          <label htmlFor="current-password">현재 비밀번호</label>
          <input id="current-password" name="currentPassword" type="password" className="form-control" autoComplete="current-password" required />
          <label htmlFor="new-password">새 비밀번호</label>
          <input id="new-password" name="newPassword" type="password" className="form-control" autoComplete="new-password" minLength={12} required />
          <label htmlFor="new-password-confirm">새 비밀번호 확인</label>
          <input id="new-password-confirm" name="newPasswordConfirm" type="password" className="form-control" autoComplete="new-password" minLength={12} required />
          <p className="small text-secondary mt-2 mb-0">12자 이상이며 영문과 숫자를 포함해야 합니다.</p>
          <button className="btn btn-primary w-100 mt-3">비밀번호 변경</button>
        </form>

        {canCreate && <form className="panel" onSubmit={create}>
          <div className="panel-title"><h3>사용자 추가</h3></div>
          <label>계정</label><input name="username" className="form-control" minLength={3} required autoComplete="off" />
          <label>표시 이름</label><input name="displayName" className="form-control" required />
          <label>초기 비밀번호</label><input name="password" type="password" className="form-control" minLength={12} required autoComplete="new-password" />
          <label>초기 비밀번호 확인</label><input name="passwordConfirm" type="password" className="form-control" minLength={12} required autoComplete="new-password" />
          {canManage ? <div className="mt-2">{roles.map((role) => <label className="form-check" key={role}>
            <input className="form-check-input" type="checkbox" name={`role-${role}`} defaultChecked={role === "viewer"} />
            <span className="form-check-label">{roleNames[role]}</span>
          </label>)}</div> : <p className="small text-secondary mt-3 mb-0">생성되는 계정은 감시자 역할입니다. 카메라 및 추가 기능 권한은 관리자가 설정합니다.</p>}
          <button className="btn btn-primary w-100 mt-3">사용자 추가</button>
        </form>}
      </div>
    </div>

    {selected && <div className="panel mt-4">
      <div className="panel-title"><h3>{selected.displayName} ({selected.username})</h3><button type="button" className="btn-close btn-close-white" onClick={() => setSelected(null)} aria-label="닫기" /></div>
      {canManage && <>
        <div className="row g-3">
          <div className="col-md-4"><label>표시 이름</label><input className="form-control" value={selected.displayName} onChange={(event) => setSelected({ ...selected, displayName: event.target.value })} /></div>
          <div className="col-md-3"><label>상태</label><select className="form-select" value={selected.active ? "active" : "inactive"} onChange={(event) => setSelected({ ...selected, active: event.target.value === "active" })}><option value="active">활성</option><option value="inactive">비활성</option></select></div>
          <div className="col-md-5"><label>역할</label><div className="d-flex gap-3 pt-2">{roles.map((role) => <label className="form-check" key={role}><input className="form-check-input" type="checkbox" checked={selected.roles.includes(role)} onChange={() => toggleRole(role)} /><span className="form-check-label">{roleNames[role]}</span></label>)}</div></div>
        </div>
        <div className="d-flex gap-2 flex-wrap mt-3">
          <button type="button" className="btn btn-primary" onClick={() => void save()}>정보 저장</button>
          {(selected.failedLoginCount > 0 || isLocked(selected)) && <button type="button" className="btn btn-outline-warning" onClick={() => void unlock(selected)}>{isLocked(selected) ? "잠금 해제" : "실패 횟수 초기화"}</button>}
          {selected.id !== currentUser.id && <button type="button" className="btn btn-outline-danger ms-auto" onClick={() => void remove(selected)}>사용자 삭제</button>}
        </div>
        {selected.id !== currentUser.id && <div className="user-password-reset mt-4">
          <h4>비밀번호 초기화</h4>
          <div className="row g-2 align-items-end">
            <div className="col-md-5"><label>새 비밀번호</label><input type="password" className="form-control" value={resetPasswordValue} onChange={(event) => setResetPasswordValue(event.target.value)} minLength={12} autoComplete="new-password" /></div>
            <div className="col-md-5"><label>새 비밀번호 확인</label><input type="password" className="form-control" value={resetPasswordConfirm} onChange={(event) => setResetPasswordConfirm(event.target.value)} minLength={12} autoComplete="new-password" /></div>
            <div className="col-md-2"><button type="button" className="btn btn-outline-light w-100" disabled={!resetPasswordValue || !resetPasswordConfirm} onClick={() => void resetPassword(selected)}>초기화</button></div>
          </div>
          <p className="small text-secondary mt-2 mb-0">초기화하면 해당 사용자의 로그인 세션과 계정 잠금이 함께 해제됩니다.</p>
        </div>}
      </>}
      <details className="mt-4" open={!canManage}><summary>최근 로그인/로그아웃 이력 ({loginEvents.length})</summary><div className="table-responsive mt-2"><table className="table table-dark table-sm mb-0"><thead><tr><th>시간</th><th>이벤트</th><th>IP</th><th>접속 환경</th></tr></thead><tbody>{loginEvents.map((event) => <tr key={event.id}><td>{formatDate(event.occurredAt)}</td><td>{loginEventName(event.eventType)}</td><td>{event.ipAddress ?? "-"}</td><td className="login-agent">{event.userAgent ?? "-"}</td></tr>)}</tbody></table></div></details>
    </div>}

    {canConfigureSecurity && security && <div className="panel mt-4">
      <div className="panel-title"><h3>계정 잠금 기준</h3><span>관리자 설정</span></div>
      <div className="row g-3">
        <div className="col-md-6"><label>로그인 실패 허용 횟수</label><input className="form-control" type="number" min="3" max="20" value={security.maxLoginFailures} onChange={(event) => setSecurity({ ...security, maxLoginFailures: Number(event.target.value) })} /></div>
        <div className="col-md-6"><label>자동 잠금 시간 (분)</label><input className="form-control" type="number" min="1" max="1440" value={security.lockMinutes} onChange={(event) => setSecurity({ ...security, lockMinutes: Number(event.target.value) })} /></div>
      </div>
      <p className="small text-secondary mt-2 mb-0">허용 횟수 이상 틀리면 지정 시간 동안 로그인할 수 없습니다. 시간이 지나면 자동 해제되며, 사용자 관리에서 즉시 해제할 수도 있습니다.</p>
      <button type="button" className="btn btn-primary mt-3" onClick={() => void saveSecurity()}>잠금 기준 저장</button>
    </div>}
  </>;
}

function isLocked(user: ManagedUser) {
  return Boolean(user.lockedUntil && new Date(user.lockedUntil).getTime() > Date.now());
}
function formatDate(value: string | null) { return value ? new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : "-"; }
function loginEventName(value: string) { return ({ login_success: "로그인 성공", login_failure: "로그인 실패", logout: "로그아웃", locked: "계정 잠김" } as Record<string, string>)[value] ?? value; }
